// hooks/useFcmToken.ts
import { useEffect, useState } from "react";
import { getToken, isSupported } from "firebase/messaging";
import { getMessagingInstance, setForegroundNotificationHandler } from "../../firebase";
import { useGetMeQuery } from "../api/auth/auth.api";
import { useToast } from "@racoon/ui";

const SW_READY_TIMEOUT_MS = 15_000;

/**
 * Wait for the service worker to be active, with timeout
 */
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser");
  }

  const readyPromise = navigator.serviceWorker.ready;
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => {
      reject(
        new Error(
          "Service worker did not become ready in time. Check that the SW activates (e.g. no runtime errors in sw.ts).",
        ),
      );
    }, SW_READY_TIMEOUT_MS),
  );

  return Promise.race([readyPromise, timeoutPromise]);
}

/**
 * Custom hook to request notification permission and get FCM token
 */
export const useFcmToken = () => {
  useGetMeQuery({});
  const { toast } = useToast();
  const [, setFcmToken] = useState<string | null>(null);

  // (1) Request permission and get token once per session
  useEffect(() => {
    let cancelled = false;
    const requestFcmToken = async () => {
      try {
        const messaging = getMessagingInstance();
        if (!messaging) {
          return;
        }
        const messagingSupported = await isSupported();
        if (!messagingSupported) {
          console.warn("Firebase Messaging is not supported in this browser");
          return;
        }
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.warn("Notification permission denied");
          return;
        }
        const registration = await getServiceWorkerRegistration();
        if (!registration.pushManager) {
          console.warn("PushManager not available on ServiceWorkerRegistration");
          return;
        }
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (!cancelled && token) {
          setFcmToken(token);
        }
      } catch (err) {
        console.error("Failed to get FCM token:", err);
      }
    };
    requestFcmToken();
    return () => {
      cancelled = true;
    };
  }, []);

  // Register toast for foreground FCM messages (firebase.ts is not a React component)
  useEffect(() => {
    setForegroundNotificationHandler((payload) => {
      toast({
        title: payload.data?.title || payload.notification?.title || "New notification",
        description: payload.data?.body || payload.notification?.body || "No body",
      });
    });
    return () => setForegroundNotificationHandler(null);
  }, [toast]);
};
